import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationsContext';
import AppShell from '@/components/ops/AppShell';
import { Archive, Inbox, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

const PRIORITY = ['admin', 'interviewer', 'production'];

function fmt(s) {
  if (!s) return '';
  const d = new Date(s);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function OpsNotifications() {
  const { user, loading: authLoading, activeRole } = useAuth();
  const navigate = useNavigate();
  const { displayFor, routeFor, markRead, refresh } = useNotifications();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readFilter, setReadFilter] = useState('all'); // all | unread | read
  const [eventFilter, setEventFilter] = useState('');
  const [eventTypes, setEventTypes] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/ops/login'); return; }
  }, [user, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [list, types] = await Promise.all([
        axios.get(`${API}/ops/notifications?limit=200&include_archived=false${eventFilter ? `&event_type=${eventFilter}` : ''}${readFilter === 'unread' ? '&unread_only=true' : ''}`, getAuth()),
        eventTypes.length === 0 ? axios.get(`${API}/ops/notifications/event-types`, getAuth()) : Promise.resolve({ data: eventTypes }),
      ]);
      let arr = list.data;
      if (readFilter === 'read') arr = arr.filter(n => n.is_read || n.read);
      setItems(arr);
      if (types?.data && eventTypes.length === 0) setEventTypes(types.data);
    } catch {}
    finally { setLoading(false); }
  }, [user, readFilter, eventFilter, eventTypes]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const archiveOne = async (id) => {
    try {
      await axios.post(`${API}/ops/notifications/${id}/archive`, {}, getAuth());
      setItems(it => it.filter(n => n.id !== id));
      refresh();
    } catch {}
  };
  const openNotif = async (n) => {
    if (!n.is_read && !n.read) {
      await markRead(n.id);
      setItems(it => it.map(x => x.id === n.id ? { ...x, is_read: true, read: true } : x));
    }
    const route = routeFor(n);
    if (route) navigate(route);
  };

  if (authLoading || !user) {
    return (
      <div className="ops-shell min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <div className="w-7 h-7 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  const role = PRIORITY.find(r => (user.roles || []).includes(r) && r === activeRole) || PRIORITY.find(r => (user.roles || []).includes(r)) || 'admin';

  return (
    <AppShell pageTitle="Notifications" subtitle={`${items.length} total`} activeNav={null} onNavigate={() => {}} role={role}>
      <div className="px-6 py-5" data-testid="notifications-inbox-page">
        <div className="flex items-center gap-2 mb-4">
          <select
            value={readFilter}
            onChange={e => setReadFilter(e.target.value)}
            data-testid="notif-filter-read"
            className="h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-700 outline-none transition-colors"
          >
            <option value="all">All</option>
            <option value="unread">Unread only</option>
            <option value="read">Read only</option>
          </select>
          <select
            value={eventFilter}
            onChange={e => setEventFilter(e.target.value)}
            data-testid="notif-filter-event"
            className="h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-700 outline-none transition-colors"
          >
            <option value="">All event types</option>
            {eventTypes.map(et => <option key={et.key} value={et.key}>{et.label}</option>)}
          </select>
          <span className="text-[12px] text-slate-500 ml-1">{items.length} result{items.length === 1 ? '' : 's'}</span>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
          {loading ? (
            <div className="py-10 text-center text-[13px] text-slate-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 px-6">
              <Inbox size={20} className="mx-auto text-slate-300 mb-2" />
              <p className="text-[14px] text-slate-700 font-medium mb-1">Nothing here</p>
              <p className="text-[12px] text-slate-500">No notifications match the current filters.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="notif-list">
              {items.map(n => {
                const d = displayFor(n);
                const unread = !(n.is_read || n.read);
                return (
                  <li key={n.id} className={`flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors ${unread ? 'bg-slate-50/50' : ''}`} data-testid={`inbox-row-${n.id}`}>
                    <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${unread ? 'bg-slate-900' : 'bg-transparent border border-slate-200'}`} />
                    <button onClick={() => openNotif(n)} className="flex-1 min-w-0 text-left">
                      <p className="text-[13px] text-slate-900 leading-tight">{d.title}</p>
                      {d.body && <p className="text-[12px] text-slate-500 leading-tight mt-0.5">{d.body}</p>}
                      <p className="text-[11px] text-slate-400 mt-1">{fmt(n.created_at)}</p>
                    </button>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {unread && (
                        <button
                          onClick={() => markRead(n.id).then(() => setItems(it => it.map(x => x.id === n.id ? { ...x, is_read: true, read: true } : x)))}
                          title="Mark read"
                          className="h-7 w-7 grid place-items-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
                          data-testid={`inbox-mark-read-${n.id}`}
                        >
                          <Check size={12} />
                        </button>
                      )}
                      <button
                        onClick={() => archiveOne(n.id)}
                        title="Archive"
                        className="h-7 w-7 grid place-items-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
                        data-testid={`inbox-archive-${n.id}`}
                      >
                        <Archive size={12} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}
